import Strings from "../core/data/Strings";
import ResizableContainer from "../core/ResizableContainer";
import ScaleManager from "../core/ScaleManager";
import GameColors from "../GameColors";
import GameScene from "../scenes/GameScene";
import ARIA from "../utils/ARIA";
import Fonts from "../utils/Fonts";
import LayoutUtils from "../utils/LayoutUtils";

export class HelpPopup extends ResizableContainer {
	scene: GameScene;
	nextPressed: boolean;

	private _onResize: () => void;

	constructor(scene: GameScene, onComplete: () => void, firstTime = false, fade = true) {
		super(scene, 0, 0);
		this.nextPressed = false;
		const ariaLayer = "help-popup";

		const overlay = scene.add.image(0, 0, "popupOverlay");
		overlay.setInteractive();
		this.add(overlay);

		const popupContainer = scene.add.container();
		this.add(popupContainer);

		const createHelpIcon = (index: number): Phaser.GameObjects.Container => {
			const container = scene.add.container(0, 0);
			const count = index + 1;

			const icon = scene.add.image(0, 0, "helpImg" + count);
			icon.setOrigin(0.5, 0.5);
			container.add(icon);

			const countBase = scene.add.image(0, -110, "helpNumberBase");
			countBase.setOrigin(0.5, 0.5);
			container.add(countBase);

			const countText = scene.add.text(0, 0, count.toString(), {
				color: GameColors.PURPLE,
				fontFamily: Fonts.MontserratExtraBold,
				fontSize: "46px",
			});
			countText.setOrigin(0.5, 0.5);
			container.add(countText);
			scene.alignIn.Center(countText, countBase, 0, 0);

			return container;
		};

		const backer = this.scene.add.image(0, 0, "storyBacker");
		popupContainer.add(backer);

		const icons: Phaser.GameObjects.Container[] = [];
		for (let i = 0; i < 3; i++) {
			const icon = createHelpIcon(i);
			popupContainer.add(icon);
			icons.push(icon);
		}

		const headerBG = scene.add.image(0, 0, "popupHeader");
		const header = scene.add.text(0, 0, Strings.get("Help"), {
			color: GameColors.WHITE,
			fontFamily: Fonts.MontserratExtraBold,
			fontSize: "68px",
		});
		header.scaleX = 0.85;
		popupContainer.add([headerBG, header]);

		const textGroup = this.scene.add.container();
		popupContainer.add(textGroup);

		const bubble = scene.add.image(0, 0, "storyTextBacker");
		textGroup.add(bubble);
		textGroup.setSize(bubble.width, bubble.height);

		const storyText = this.scene.add.text(0, 0, Strings.get("HelpText"), {
			color: GameColors.PURPLE,
			fontSize: "36px",
			fontFamily: Fonts.MontserratBold,
			align: "center",
			wordWrap: { width: bubble.width - 150 },
		});
		textGroup.add(storyText);

		const next = (): void => {
			if (firstTime && !this.nextPressed) {
				storyText.text = Strings.get("HelpText2");
				storyText.focusARIA();
				this.nextPressed = true;
			} else {
				if (fade) {
					this.scene.add.tween({
						targets: overlay,
						alpha: 0,
						duration: 300,
					});
				}
				this.scene.add.tween({
					targets: popupContainer,
					y: { from: popupContainer.y, to: popupContainer.y + 1000 },
					duration: 400,
					ease: Phaser.Math.Easing.Back.In,
					onComplete: () => {
						overlay.destroy();
						popupContainer.destroy();
						onComplete();
					},
				});
			}
		};

		const nextButton = scene.add.button(0, 0, "nextBtn", 0, next, this, true);
		popupContainer.add(nextButton);

		this._onResize = (): void => {
			ScaleManager.ScaleBackgroundFromZone(overlay, scene.viewport);
			scene.alignIn.Center(overlay, scene.viewport);
			scene.alignIn.Center(popupContainer, scene.viewport);

			scene.alignIn.TopCenter(headerBG, backer, 0, headerBG.height / 2 - 10);
			scene.alignIn.Center(header, headerBG, 0, 0);

			this.scene.alignIn.Center(textGroup, backer, 0, 30);
			this.scene.alignIn.Center(bubble, textGroup);
			this.scene.alignIn.Center(storyText, bubble);

			LayoutUtils.alignGrid(scene, icons, backer, { x: 360, y: 400 }, 3, 0, -215);

			scene.alignIn.BottomCenter(nextButton, backer, 0, 50);
		};
		this.resize();

		header.setARIA(ariaLayer, true);
		storyText.setARIA(ariaLayer);
		nextButton.setARIA("Next", ariaLayer);
		ARIA.showLayer(ariaLayer, true);

		if (fade) {
			this.scene.tweens.add({
				targets: overlay,
				alpha: 0.5,
				duration: 200,
			});
			overlay.alpha = 0;
		} else overlay.alpha = 0.5;

		this.scene.tweens.add({
			targets: popupContainer,
			y: popupContainer.y,
			duration: 500,
			ease: Phaser.Math.Easing.Back.Out,
		});
		popupContainer.y += 1000;
	}

	resize(): void {
		super.resize();
		if (this._onResize) this._onResize();
	}
}
